import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import { useTransactionManager } from 'components/WorkspaceEditor/services'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import {
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from 'components/WorkspaceEditor/types/types'
import { getRootArticleContentItemIds } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { TrackedChange } from 'models/changes'
import { useCallback, useState } from 'react'
import { acceptRejectTrackChanges } from '../api'
import { useTreeMutationsWithTrackChanges } from '../services'
import { Action } from '../types/types'
import { processNonSiblingElementsForTrackChanges } from '../utils/TrackChangesPanelUtils'

export interface UpdatedTrackChangesData {
    trackChangesData: TrackedChange[]
    action: Action
}

/**
 * Custom hook to handle card actions in the Track Changes panel.
 * Manages optimistic updates and fetches content item view nodes after actions.
 */
export const useHandleCardActions = () => {
    const { contentItemViewNodes } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
    }))
    const { executeWithOptimistic } = useTransactionManager()
    const { onContentViewFetchSuccess } = useProductFetcherUtils({
        overrideContentItemNodes: false,
    })
    const { acceptRejectTrackChangesData } = acceptRejectTrackChanges()
    const { updateTreeViewNodes } = useTreeMutationsWithTrackChanges()
    const { trackChangesData } = useSuggestionsShallowSelector((state) => ({
        trackChangesData: state.trackChangesData,
    }))
    const [updatedTrackChangesData, setUpdatedTrackChangesData] = useState<
        UpdatedTrackChangesData
    >()
    const { getContentItemViewNodesAPI } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            const optimisticTreeViewNodes = await onContentViewFetchSuccess(
                response,
                [],
                true
            )
            if (optimisticTreeViewNodes) {
                updateTreeViewNodes(
                    optimisticTreeViewNodes,
                    updatedTrackChangesData
                )
            }
        },
    })

    // Handles optimistic UI update after a successful track change action
    const onSuccessOptimisticUpdate = useCallback(
        async (ids: string[], action: Action, resultData: TrackedChange[]) => {
            let updatedTcData = updatedTrackChangesData
            if (!updatedTrackChangesData?.trackChangesData.length) {
                setUpdatedTrackChangesData({
                    trackChangesData: resultData,
                    action,
                })
                updatedTcData = {
                    trackChangesData: resultData,
                    action,
                }
            }
            const {
                nonSiblingElements,
                updatedContentItem,
            } = processNonSiblingElementsForTrackChanges(
                contentItemViewNodes,
                ids,
                action
            )
            const nonSiblingIds = Object.keys(nonSiblingElements)
            const filteredIds = ids.filter((id) => !nonSiblingIds.includes(id))
            if (filteredIds.length === 0) {
                return updateTreeViewNodes(updatedContentItem, updatedTcData)
            }
            const rootArticleContentItemIds = getRootArticleContentItemIds(
                contentItemViewNodes,
                filteredIds
            )
            try {
                await getContentItemViewNodesAPI(rootArticleContentItemIds)
            } catch (error) {
                console.error('Error fetching content item view nodes:', error)
                updateTreeViewNodes(updatedContentItem, updatedTcData)
            }
        },
        [
            getContentItemViewNodesAPI,
            updateTreeViewNodes,
            contentItemViewNodes,
            updatedTrackChangesData,
            setUpdatedTrackChangesData,
        ]
    )

    /**
     * Handles actions on suggestions in content items with optimistic UI and error fallback.
     */
    const handleSuggestionsInContentItem = useCallback(
        async (ids: string[], action: Action, trackChangesId?: string) => {
            const backupContentItemViewNodes = [...contentItemViewNodes]
            let resultData: TrackedChange[] = []
            await executeWithOptimistic({
                fallbackState: JSON.stringify(contentItemViewNodes),
                asyncAction: async () => {
                    const result = await acceptRejectTrackChangesData(
                        trackChangesId as string,
                        action
                    )
                    resultData =
                        result?.data?.acceptTrackChange ||
                        result?.data?.rejectTrackChange ||
                        result?.data?.acceptAllTrackChanges ||
                        result?.data?.rejectAllTrackChanges ||
                        []
                },
                successCbFn: async () => {
                    setUpdatedTrackChangesData({
                        trackChangesData: resultData,
                        action,
                    })
                    return onSuccessOptimisticUpdate(ids, action, resultData)
                },
                errorCbFn: () =>
                    updateTreeViewNodes(backupContentItemViewNodes),
            })
        },
        [
            contentItemViewNodes,
            trackChangesData,
            executeWithOptimistic,
            acceptRejectTrackChangesData,
            onSuccessOptimisticUpdate,
            updateTreeViewNodes,
            setUpdatedTrackChangesData,
        ]
    )

    return { handleSuggestionsInContentItem }
}
